import {
  InvoiceDeliveryMethod,
  InvoiceFrequency,
  InvoicePaymentTerms,
  InvoiceToSchema,
  RecurringInvoiceIntervalDays,
} from "@/queries/invoice/types";
import { addDays, startOfDay } from "date-fns";
import { z } from "zod";

export const CreateInvoiceFormSchema = z
  .object({
    status: z.enum(["draft", "send"]),
    invoiceTo: InvoiceToSchema,
    notes: z.string().optional(),
    // transactionFee: z.boolean(),
    deliveryMethod: z.nativeEnum(InvoiceDeliveryMethod).optional().nullable(),
    invoiceFrequency: z.nativeEnum(InvoiceFrequency),
    terms: z.nativeEnum(InvoicePaymentTerms).optional(),
    dueDate: z.date().optional(),
    sendDate: z.date().optional(),
    shippingFee: z.number({ message: "Shipping Fee should be a number." }),
    invoiceFrequencyInterval: z.number().int(),
    invoiceNumber: z
      .string()
      .max(12)
      .nullable()
      .transform((val) => (val === "" ? null : val)),
    invoiceExternalId: z
      .string()
      .max(12)
      .nullable()
      .transform((val) => (val === "" ? null : val)),
    invoiceExternalIdLink: z
      .string()
      .optional()
      .transform((val) => (val === "" || val === undefined ? null : val))
      .nullable()
      .refine(
        (val) => {
          if (val === null || val === "") return true;
          // Check for dot followed by at least 2 characters
          return /\.[a-zA-Z]{2,}/.test(val);
        },
        {
          message: "Reference link must be a valid URL.",
        }
      ),
    taxPercentage: z.number({
      message: "Tax Percentage should be a number value.",
    }),
    taxAmount: z.number({ message: "Tax Amount should be a number value." }),
    lineItems: z.array(
      z.object({
        description: z.string(),
        quantity: z
          .number({ message: "Quantity should be a number value." })
          .int({ message: "Quantity should be an integer." }),
        unit_price: z.number({
          message: "Unit Price should be a number value.",
        }),
        item_total: z.number({
          message: "Item Total should be a number value.",
        }),
      })
    ),
    enableCardPayment: z.boolean().optional(),
    price: z.number().optional(),
    // .min(1, "At least one line item is required"),
    startDate: z.date().optional(),
    recurringIntervalDays: z.number().int().optional(),
  })
  .superRefine((data, ctx) => {
    // Conditional validation for sendDate when invoiceFrequency is scheduled
    if (data.invoiceFrequency === "scheduled") {
      if (!data.sendDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sendDate"],
          message: "Send date is required for scheduled invoices",
        });
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        if (data.sendDate < tomorrow) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sendDate"],
            message: "Send date must be at least one day in the future",
          });
        }
      }
    }

    if (data.status === "send" && !data.deliveryMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["deliveryMethod"],
        message: "Delivery method is required when sending invoice.",
      });
    }

    if (
      data.status === "send" &&
      data.invoiceFrequency === InvoiceFrequency.SCHEDULED &&
      data.sendDate
    ) {
      const tomorrow = startOfDay(addDays(new Date(), 1));
      if (data.sendDate < tomorrow) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sendDate"],
          message:
            "Send date must be tomorrow or later for scheduled invoices.",
        });
      }
    }

    if (data.status === "send" && !data.dueDate && !data.terms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["terms"],
        message: "Terms or Due Date must be specified.",
      });
    }

    if (data.status === "send" && data.dueDate && data.terms) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["terms"],
        message: "Terms and Due Date cannot both be selected.",
      });
    }

    if (data.status === "send" && data.dueDate && data.sendDate) {
      if (data.sendDate >= data.dueDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["terms"],
          message: "Due date must be at least one day later from date sent.",
        });
      }
    }

    // Validation for non-empty line items
    if (data.status === "send" && data.lineItems.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lineItems"],
        message: "At least one line item is required",
      });
    }
    if (data.status === "send") {
      data.lineItems.forEach((item, i) => {
        if (!item.description) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`lineItems.${i}.description`],
            message: "Description required.",
          });
        }

        if (item.quantity <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`lineItems.${i}.quantity`],
            message: "Quantity must be greater than 0.",
          });
        }
      });
    }

    // Conditional validation for taxPercentage
    if (data.status === "send" && data.taxAmount && data.taxPercentage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["taxPercentage"],
        message: "Only one option should be selected",
      });
    }

    // Conditional validation for recurring invoices
    if (data.invoiceFrequency === InvoiceFrequency.RECURRING) {
      const validIntervals = Object.values(RecurringInvoiceIntervalDays).filter(
        (v): v is number => typeof v === "number"
      );
      if (
        !data.recurringIntervalDays ||
        !validIntervals.includes(data.recurringIntervalDays)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recurringIntervalDays"],
          message: "Select a valid interval for recurring invoices.",
        });
      }
      if (!data.deliveryMethod) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["deliveryMethod"],
          message: "Delivery method is required for recurring invoices.",
        });
      }
    }
  });

// Type for form data
export type CreateInvoiceFormData = z.infer<typeof CreateInvoiceFormSchema>;
